import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata
} from '../../client-server-common';
import type {Values, RichTextContent, HideableValue } from '@wix/devzai-utils-common';
import {urlRemoveQueryString, hideableValueGetValue, hideableValueIsVisible } from '@wix/devzai-utils-common';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';


export const SoundcloudWidgetSize = {
    Big: 'Big',
    Normal: 'Normal',
    Compact: 'Compact',
} as const;


export namespace DoppeActionTypeSoundCloud {

    export type Size = Values<typeof SoundcloudWidgetSize>;

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings  {
        url: string;
        size: Size;
        title: HideableValue<string>;
        description: HideableValue<RichTextContent>;
    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        buttonText?: HideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeSoundcloud = doppeActionTypeDefineMetadata<DoppeActionTypeSoundCloud.ActionSettings, DoppeActionTypeSoundCloud.PartialProps>({
    id: 'io.bymo.action/soundcloud',
    resolveActionSettings: action => {

        const {
            buttonText = '',
            ctaButton,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        return {
            url: '',
            size: SoundcloudWidgetSize.Normal,
            title: 'Title',
            description: 'Description',
            buttonText: buttonText,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: hideableValueGetValue(buttonText),
                showButton: hideableValueIsVisible(buttonText),
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }

    },
    hasNoGoalMeasuring: false,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl, {
                description: 'urlFormat.soundcloud',
                aiValidator: (value) => {
                    if (typeof value !== 'string') {
                        return `Expected a URL string, got ${typeof value}`;
                    }
                    if (!doppeValidateSoundcloudURL(value)) {
                        return `Must be a SoundCloud URL on soundcloud.com`;
                    }
                    return null;
                },
            }),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                description: true,
                title: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    hasNoLockInnerCTA: true,
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.url ?? null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.url = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
})

export function doppeValidateSoundcloudURL(url: string) {
    return doppeValidateSoundcloudTrackURL(url) || doppeValidateSoundcloudPlaylistURL(url);
}

export function doppeValidateSoundcloudTrackURL(url: string) {
    const trackPattern = /^https:\/\/soundcloud\.com\/[\w-]+\/[\w-]+$/;
    return trackPattern.test(urlRemoveQueryString(url));
}

export function doppeValidateSoundcloudPlaylistURL(url: string) {
    const playlistPattern = /^https:\/\/soundcloud\.com\/[\w-]+\/sets\/[\w-]+$/;
    return playlistPattern.test(urlRemoveQueryString(url));
}